import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, ComponentPropsWithoutRef, useState } from 'react';
import styled from 'styled-components';

import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';

const Container = styled.div`
  display: grid;
  grid-template-columns: 100px 130px;
  grid-gap: ${Spacing[2]} ${Spacing[4]};
  align-items: center;
  min-height: ${({ theme }) => theme.element.height.md};
`;

export enum TimeIntervalInputUnit {
  SECONDS = 'seconds',
  DAYS = 'days',
  WEEKS = 'weeks',
  YEARS = 'years',
}

const TIME_UNIT_IN_SECONDS = {
  [TimeIntervalInputUnit.SECONDS]: 1,
  [TimeIntervalInputUnit.DAYS]: 86400,
  [TimeIntervalInputUnit.WEEKS]: 604800,
  [TimeIntervalInputUnit.YEARS]: 31536000,
};

const TIME_INTERVAL_OPTIONS = [
  { label: 'day(s)', value: TimeIntervalInputUnit.DAYS },
  { label: 'week(s)', value: TimeIntervalInputUnit.WEEKS },
  { label: 'year(s)', value: TimeIntervalInputUnit.YEARS },
];

interface TimeIntervalInputProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'onChange' | 'value'> {
  id?: string;
  value?: number;
  unit: TimeIntervalInputUnit;
  hasError?: boolean;
  isDirty?: boolean;
  onChange?: (value: number) => void;
}

const getSaneUnitOfTime = (
  value: number,
  valueUnit: TimeIntervalInputUnit,
): TimeIntervalInputUnit => {
  if (value == null || isNaN(value)) {
    return TimeIntervalInputUnit.DAYS;
  }

  const valueInSeconds = value * TIME_UNIT_IN_SECONDS[valueUnit];
  if (valueInSeconds % TIME_UNIT_IN_SECONDS[TimeIntervalInputUnit.YEARS] === 0) {
    return TimeIntervalInputUnit.YEARS;
  } else if (valueInSeconds % TIME_UNIT_IN_SECONDS[TimeIntervalInputUnit.WEEKS] === 0) {
    return TimeIntervalInputUnit.WEEKS;
  } else {
    return TimeIntervalInputUnit.DAYS;
  }
};

const convertValue = (
  value: number,
  currentUnit: TimeIntervalInputUnit,
  desiredUnit: TimeIntervalInputUnit,
) => {
  if (isNaN(value) || value == null) {
    return 0;
  }
  return value * (TIME_UNIT_IN_SECONDS[currentUnit] / TIME_UNIT_IN_SECONDS[desiredUnit]);
};

const TimeIntervalInput = ({
  id,
  name,
  disabled,
  value,
  unit,
  hasError,
  onChange,
  onBlur,
  min,
}: TimeIntervalInputProps) => {
  const [selectedUnit, setSelectedUnit] = useState<TimeIntervalInputUnit>(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    getSaneUnitOfTime(value, unit),
  );
  const [inputValue, setInputValue] = useState<string>(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    convertValue(value, unit, selectedUnit).toString(),
  );

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.currentTarget.value);
    const newInputValue = event.currentTarget.valueAsNumber;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onChange(convertValue(!isNaN(newInputValue) ? newInputValue : 0, selectedUnit, unit));
  };

  const handleSelectChange = (newUnit: TimeIntervalInputUnit) => {
    const input = parseInt(inputValue);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onChange(convertValue(!isNaN(input) ? input : 0, newUnit, unit));
    setSelectedUnit(newUnit);
  };

  return (
    <Container>
      <Input
        data-test={`${id}`}
        id={`${id}`}
        name={`${name}`}
        type='number'
        min={min}
        value={inputValue.toString()}
        hasError={hasError}
        onBlur={onBlur}
        onChange={handleInputChange}
        placeholder='#'
        disabled={disabled}
      />
      <Select
        data-test={`${id}_select`}
        name={`${name}_select`}
        onValueChange={handleSelectChange}
        value={selectedUnit}
        disabled={disabled}
      >
        {TIME_INTERVAL_OPTIONS.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </Container>
  );
};

export default TimeIntervalInput;
